import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Field, Form, Icon, Input, TextArea, Checkbox } from '@/components';

import { PortfolioSummary, Project } from '@/features/projects/types';

import { formatStringAsInteger, nullableStringSetter, parseStringToInteger } from '@/helpers';
import { useCountries } from '@/hooks';
import { extractCountryCodes, extractTechCodes, toCountryJunction, toTechJunction } from '@/features/projects/helpers';

export type PortfolioSummarySubmitFields = {
  technologies: { technologies_code: string }[];
  markets: { countries_code: string }[];
  total_mw: number;
  details: string;
};

export type PortfolioSummaryFormFields = {
  technologies: string[];
  markets: string[];
  total_mw: number;
  details: string;
};

export type PortfolioSummaryFormProps = {
  readonly id: string;
  readonly project: Project;
  readonly onSubmit: (data: any) => void;
};

export function PortfolioSummaryForm({ id, project, onSubmit }: PortfolioSummaryFormProps) {
  const { t } = useTranslation(['projects']);
  const item = project.portfolio_summary?.[0] ?? ({} as Partial<PortfolioSummary>);
  const { options: countriesOptions } = useCountries();

  const techList: { key: string; label: string; icon: string }[] = [
    { key: 'solar', label: 'Solar', icon: 'solar' },
    { key: 'wind', label: 'Wind', icon: 'wind' },
    { key: 'bess', label: 'BESS', icon: 'bess' },
    { key: 'biogas', label: 'Biogas', icon: 'biogas' },
    { key: 'hydrogen', label: 'Green Hydrogen', icon: 'hydrogen' },
    { key: 'hidropower', label: 'Hydropower', icon: 'hidropower' },
    { key: 'datacenter', label: 'Data Center', icon: 'datacenter' },
    { key: 'topco', label: 'TopCo', icon: 'topco' },
  ];

  const options = useMemo(
    () => ({
      defaultValues: {
        technologies: extractTechCodes(item.technologies),
        markets: extractCountryCodes(item.markets),
        total_mw: item.total_mw ?? null,
        details: item.details ?? null,
      },
    }),
    [item]
  );

  const onSubmitPortfolioSummaryForm = useCallback(
    (data: PortfolioSummaryFormFields) => {
      const payload = {
        technologies: toTechJunction(data.technologies),
        markets: toCountryJunction(data.markets),
        total_mw: data.total_mw,
        details: data.details,
      };

      onSubmit(payload);
    },
    [onSubmit]
  );

  return (
    <Form<PortfolioSummaryFormFields> id={id} onSubmit={onSubmitPortfolioSummaryForm} options={options}>
      {({ formState, register }) => (
        <div className="space-y-6">
          <Field id="technologies" label={t('Technologies')} classes={{ field: 'space-y-1' }}>
            <div className="mt-2 grid grid-cols-2 gap-4 sm:grid-cols-3">
              {techList.map((tech) => (
                <Checkbox
                  key={tech.key}
                  value={tech.key}
                  registration={register('technologies')}
                  color="primary"
                  classes={{ main: 'items-center' }}
                >
                  <div className="flex items-center gap-x-2">
                    <Icon name={tech.icon as any} className="text-primary-700 h-6 w-6" />
                    <span>{tech.label}</span>
                  </div>
                </Checkbox>
              ))}
            </div>
          </Field>

          <Field id="markets" label={t('Markets')} classes={{ field: 'space-y-1' }}>
            <div className="mt-2 grid grid-cols-2 gap-4 sm:grid-cols-3">
              {countriesOptions.map((country) => (
                <Checkbox
                  key={country.value}
                  value={country.value}
                  registration={register('markets')}
                  color="primary"
                  classes={{ main: 'items-center' }}
                >
                  <span>{country.label}</span>
                </Checkbox>
              ))}
            </div>
          </Field>
          <Field
            id="total_mw"
            label={t('Total MW')}
            error={formState.errors['total_mw']}
            classes={{ field: 'space-y-1' }}
          >
            <Input
              id="total_mw"
              type="text"
              mask={formatStringAsInteger}
              placeholder={t('e.g. 100')}
              color={formState.errors['total_mw'] ? 'error' : 'primary'}
              suffix="MW"
              registration={register('total_mw', { setValueAs: parseStringToInteger })}
            />
          </Field>

          <Field id="details" label={t('Details')} error={formState.errors['details']} classes={{ field: 'space-y-1' }}>
            <TextArea
              id="details"
              color={formState.errors['details'] ? 'error' : 'primary'}
              registration={register('details', { setValueAs: nullableStringSetter })}
              className="min-h-32"
            />
          </Field>
        </div>
      )}
    </Form>
  );
}
